import { PayloadAction } from '@reduxjs/toolkit';
import createAsyncSlice from '../createAsyncSlice';
import {
  loginUser,
  createUser,
  getUserByUsername,
  getUserByUid,
  followEvent,
  updateUser,
  deleteUser,
  getAllUsers,
} from '../../services/userService';
import { User } from '../../types';

/**
 * Utility function to load user from local storage
 */
const loadUserFromLocalStorage = (): User | null => {
  const storedUser = localStorage.getItem('currentUser');
  return storedUser ? JSON.parse(storedUser) : null;
};

/**
 * Utility function to save user to local storage
 */
const saveUserToLocalStorage = (user: User | null) => {
  if (user) {
    localStorage.setItem('currentUser', JSON.stringify(user));
  } else {
    localStorage.removeItem('currentUser');
  }
};

/**
 * Represents the state of the user slice.
 */
export interface UserSliceState {
  ulist: User[] | undefined;
  currentUser: User | null;
  status: 'idle' | 'loading' | 'failed';
  error: string | null;
  loading: boolean;
  viewUser: User | null;
  openUsersListModal: boolean;
  usersListView: 'followers' | 'following';
  userSearchQuery: string;
  following: boolean;
  unfollowing: boolean;
}

/**
 * Represents the initial state of the user slice.
 */
const initialState: UserSliceState = {
  ulist: [],
  currentUser: loadUserFromLocalStorage(), // Load user from local storage initially
  status: 'idle',
  error: null,
  loading: !loadUserFromLocalStorage(),
  viewUser: loadUserFromLocalStorage(),
  openUsersListModal: false,
  usersListView: 'followers',
  userSearchQuery: '',
  following: false,
  unfollowing: false,
};

/**
 * Creates reducers and selectors for managing user state.
 */
export const userSlice = createAsyncSlice({
  name: 'auth',
  initialState,
  reducers: create => ({
    login: create.asyncThunk(
      async (credentials: { username: string; password: string }) => {
        if (
          !credentials.username ||
          credentials.username === '' ||
          !credentials.password ||
          credentials.password === ''
        ) {
          return null;
        }
        const res = await loginUser(credentials.username, credentials.password);
        return res;
      },
      {
        pending: state => {
          state.status = 'loading';
          state.error = null;
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          state.currentUser = action.payload;
          saveUserToLocalStorage(action.payload); // Save user to local storage on login success
        },
        rejected: (state, action) => {
          state.status = 'failed';
          state.error = action.error.message || 'Login failed';
        },
      },
    ),
    createAccount: create.asyncThunk(
      async (data: { username: string; password: string; profilePicture: string }) => {
        if (
          !data.username ||
          data.username === '' ||
          !data.password ||
          data.password === '' ||
          data.profilePicture === '' ||
          !data.profilePicture
        ) {
          return null;
        }
        const res = await createUser(data.username, data.password, data.profilePicture);
        return res;
      },
      {
        pending: state => {
          state.status = 'loading';
          state.error = null;
        },
        fulfilled: state => {
          state.status = 'idle';
        },
        rejected: (state, action) => {
          state.status = 'failed';
          state.error = action.error.message || 'Create an Account failed';
        },
      },
    ),
    // Updates the state by fetching all users from the database
    fetchUsers: create.asyncThunk(
      async () => {
        const res = await getAllUsers();

        return res;
      },
      {
        pending: state => {
          state.status = 'loading';
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          state.ulist = action.payload;
        },
        rejected: state => {
          state.status = 'failed';
        },
      },
    ),
    fetchProfileUser: create.asyncThunk(
      async (data: { identifier: string; idType: 'username' | '_id' }) => {
        if (!data.identifier) return null;
        const res = await (data.idType === 'username'
          ? getUserByUsername(data.identifier)
          : getUserByUid(data.identifier));
        return res;
      },
      {
        pending: state => {
          state.status = 'loading';
          state.error = null;
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          state.viewUser = action.payload;
        },
        rejected: (state, action) => {
          state.status = 'failed';
          state.error = action.error.message || `Error fetching user`;
        },
      },
    ),
    setProfileUser: create.reducer((state, action: PayloadAction<User | undefined>) => {
      if (action.payload) {
        state.viewUser = action.payload;
        const newList = state.ulist?.filter(u => u._id !== (action.payload as User)._id);
        if (action.payload) newList?.push(action.payload);
        state.ulist = newList;
      }
    }),
    setFollowButtonLoading: create.reducer(
      (state, action: PayloadAction<'follow' | 'unfollow'>) => {
        if (action.payload === 'follow') {
          state.following = true;
        } else {
          state.unfollowing = true;
        }
      },
    ),
    fetchUpdatedFollowers: create.asyncThunk(
      async (data: { follower: string; following: string; action: 'follow' | 'unfollow' }) => {
        const res = await followEvent(data.follower, data.following, data.action);
        return res;
      },
      {
        pending: state => {
          state.status = 'loading';
          state.error = null;
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          [state.currentUser, state.viewUser] = action.payload;
          state.following = false;
          state.unfollowing = false;
          saveUserToLocalStorage(action.payload[0]);
        },
        rejected: (state, action) => {
          state.status = 'failed';
          state.error = action.error.message || `Error updating followers`;
        },
      },
    ),
    logout: create.reducer(state => {
      state.currentUser = null;
      state.status = 'idle';
      state.error = null;
      saveUserToLocalStorage(null); // Clear user from local storage on logout
    }),
    clearError: create.reducer(state => {
      state.error = null; // Clear any existing error
    }),
    setCurrUser: create.asyncThunk(
      async (data: { identifier: string; idType: 'username' | '_id' }) => {
        if (!data.identifier) return null;
        const res = await (data.idType === 'username'
          ? getUserByUsername(data.identifier)
          : getUserByUid(data.identifier));
        return res;
      },
      {
        pending: state => {
          state.status = 'loading';
          state.error = null;
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          state.currentUser = action.payload;
          const newList = state.ulist?.filter(u => u._id !== (action.payload as User)._id);
          newList?.push(action.payload);
          state.ulist = newList;
          saveUserToLocalStorage(action.payload);
        },
        rejected: (state, action) => {
          state.status = 'failed';
          state.currentUser = null;
          state.error = action.error.message || 'User not found';
        },
      },
    ),
    addOneToScore: create.reducer(state => {
      if (state.currentUser) state.currentUser.score += 1;
    }),
    updateUserFields: create.asyncThunk(
      async (data: {
        uid: string;
        field: 'username' | 'password' | 'profilePicture' | 'score';
        updatedValue: string;
      }): Promise<User> => {
        const res = await updateUser(data.uid, data.field, data.updatedValue);
        return res;
      },
      {
        pending: state => {
          state.status = 'loading';
          state.error = null;
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          state.currentUser = action.payload;
          state.viewUser = action.payload;
          saveUserToLocalStorage(action.payload);
        },
        rejected: (state, action) => {
          state.status = 'failed';
          state.error = action.error.message || `Error updating user settings`;
        },
      },
    ),
    deleteAccount: create.asyncThunk(
      async (uid: string): Promise<{ acknowledged: boolean; deletedCount: number }> => {
        const res = await deleteUser(uid);
        return res;
      },
      {
        pending: state => {
          state.status = 'loading';
          state.error = null;
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          if (action.payload.acknowledged) {
            state.currentUser = null;
            saveUserToLocalStorage(null);
          }
        },
        rejected: (state, action) => {
          state.status = 'failed';
          state.error = action.error.message || `Error updating user settings`;
        },
      },
    ),
    setOpenUsersListModal: create.reducer((state, action: PayloadAction<boolean>) => {
      state.openUsersListModal = action.payload;
    }),
    setUsersListView: create.reducer((state, action: PayloadAction<'followers' | 'following'>) => {
      state.usersListView = action.payload;
    }),
    setUserSearchQuery: create.reducer((state, action: PayloadAction<string>) => {
      state.userSearchQuery = action.payload;
    }),
  }),
  selectors: {
    ulistSelector: state => state.ulist,
    ulistBySearchSelector: state =>
      state.ulist?.filter((user: User) =>
        user?.username.toLowerCase().startsWith(state.userSearchQuery.toLowerCase()),
      ),
    currentUserSelector: state => state.currentUser,
    authStatusSelector: state => state.status,
    authErrorSelector: state => state.error,
    viewUserSelector: state => state.viewUser,
    openUsersListModalSelector: state => state.openUsersListModal,
    usersListViewSelector: state => state.usersListView,
    userSearchQuerySelector: state => state.userSearchQuery,
    followingSelector: state => state.following,
    unfollowingSelector: state => state.unfollowing,
  },
});

export const {
  login,
  createAccount,
  fetchUsers,
  fetchProfileUser,
  setProfileUser,
  setCurrUser,
  setFollowButtonLoading,
  fetchUpdatedFollowers,
  logout,
  clearError,
  addOneToScore,
  updateUserFields,
  deleteAccount,
  setOpenUsersListModal,
  setUsersListView,
  setUserSearchQuery,
} = userSlice.actions;
export const {
  ulistSelector,
  ulistBySearchSelector,
  currentUserSelector,
  authStatusSelector,
  authErrorSelector,
  viewUserSelector,
  openUsersListModalSelector,
  usersListViewSelector,
  userSearchQuerySelector,
  followingSelector,
  unfollowingSelector,
} = userSlice.selectors;
